import { GRID_EDIT_START_EVENT } from "@hooks";
import { useAnnounce } from "@src/components/live-announcer";
import { NO_ESCAPE_OPTIONS } from "@src/raya-lucaria/i18n/interpolation";
import { Cell, Row } from "@tanstack/react-table";
import { KeyboardEvent, useCallback, useEffect, useRef } from "react";

import { useTableContext } from "../../context";
import { getColumnLabel, resolveRowLabel } from "../../utils";

interface CellEditShellOptions<T> {
  row: Row<T>;
  cell: Cell<T, unknown>;
  savesImmediately?: boolean;
}

interface DeferredCellEditOptions<T> extends CellEditShellOptions<T> {
  isChanged: () => boolean;
  commit: () => void;
  restore: () => void;
  describeValue: () => string;
}

function useCellEditShell<T, E extends HTMLElement>({
  row,
  cell,
  savesImmediately = false,
}: CellEditShellOptions<T>) {
  const { t, table } = useTableContext();
  const announce = useAnnounce();
  const controlRef = useRef<E>(null);
  const column = getColumnLabel(cell.column);
  const rowLabel = resolveRowLabel(t, table, row);

  useEffect(() => {
    const control = controlRef.current;
    if (!control) return;
    const announceStart = () =>
      announce(
        savesImmediately
          ? t("cellEditStartImmediate", { column, ...NO_ESCAPE_OPTIONS })
          : t("cellEditStart", { column, ...NO_ESCAPE_OPTIONS }),
      );
    control.addEventListener(GRID_EDIT_START_EVENT, announceStart);
    return () =>
      control.removeEventListener(GRID_EDIT_START_EVENT, announceStart);
  }, [announce, column, savesImmediately, t]);

  const focusCell = useCallback(() => {
    controlRef.current?.closest<HTMLElement>("td, th")?.focus();
  }, []);

  return {
    t,
    announce,
    column,
    controlRef,
    focusCell,
    label: t("cellEditLabel", { column, row: rowLabel, ...NO_ESCAPE_OPTIONS }),
  };
}

export function useCellEdit<T, E extends HTMLElement>({
  row,
  cell,
  isChanged,
  commit,
  restore,
  describeValue,
}: DeferredCellEditOptions<T>) {
  const { t, announce, column, controlRef, focusCell, label } =
    useCellEditShell<T, E>({ row, cell });
  const finished = useRef(false);

  const announceCommitted = useCallback(() => {
    announce(
      t("cellEditCommitted", {
        column,
        value: describeValue(),
        ...NO_ESCAPE_OPTIONS,
      }),
    );
  }, [announce, column, describeValue, t]);

  const finishWithCommit = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    if (isChanged()) {
      commit();
      announceCommitted();
    } else {
      announce(t("cellEditUnchanged", { column, ...NO_ESCAPE_OPTIONS }));
    }
    focusCell();
  }, [announce, announceCommitted, column, commit, focusCell, isChanged, t]);

  const finishWithCancel = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    restore();
    announce(t("cellEditCancelled", { column, ...NO_ESCAPE_OPTIONS }));
    focusCell();
  }, [announce, column, focusCell, restore, t]);

  const onKeyDown = (event: KeyboardEvent<E>) => {
    if (event.key !== "Enter" && event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    if (event.key === "Enter") {
      finishWithCommit();
    } else {
      finishWithCancel();
    }
  };

  const onBlur = () => {
    if (finished.current) return;
    finished.current = true;
    if (isChanged()) {
      commit();
      announceCommitted();
    }
  };

  const onFocus = () => {
    finished.current = false;
  };

  return {
    controlProps: {
      ref: controlRef,
      "aria-label": label,
      onKeyDown,
      onBlur,
      onFocus,
    },
  };
}

export function useCellFocusReturn<T, E extends HTMLElement>({
  row,
  cell,
}: CellEditShellOptions<T>) {
  const { controlRef, focusCell, label } = useCellEditShell<T, E>({
    row,
    cell,
    savesImmediately: true,
  });

  const onKeyDown = (event: KeyboardEvent<E>) => {
    if (event.key !== "Enter" && event.key !== "Escape") return;
    event.preventDefault();
    focusCell();
  };

  return {
    controlProps: { ref: controlRef, "aria-label": label, onKeyDown },
  };
}
